// ============================================================
// water-setting.js
// ------------------------------------------------------------
// ตั้งค่าค่าใช้จ่ายของโครงการ (จอ "จัดการอัตราค่าน้ำ") ที่หน้าอื่นต้องเรียกใช้
// เดิม 11 หน้ายิง API v1/TH/water-manage/detail/<water_id> เองทุกหน้า
// และเขียนสูตรคิดค่าส่วนกลาง/ยอดรวมบิลซ้ำกันเอง
//
// ย้ายมารวมที่นี่เพื่อให้แก้สูตรที่เดียวแล้วตรงกันทุกหน้า
// ============================================================

import { GET } from "../core/api.js";
import { tokens, getStorage } from "../core/storage.js";
import { roundToTwo } from "../core/format.js";

// ดึงตั้งค่าของโครงการที่กำลังใช้งานอยู่ (คืน null ถ้าดึงไม่ได้)
export const getWaterSetting = async (water_id) => {
  const result = await GET(tokens, "v1/TH/water-manage/detail/" + (water_id || getStorage("water_id")), null);
  return result?.status ? result.data.data : null;
};

// ค่าส่วนกลางของห้องหนึ่ง — ใช้ร่วมกันทุกหน้า (จดมิเตอร์ / จดแบบพัก / นำเข้าไฟล์)
//   common_expenses_type = 0 → คิดเหมาต่อห้อง ใช้ค่า common_expenses ตรง ๆ
//   common_expenses_type = 1 → คิดตามขนาดห้อง (ตรม.) = ขนาดห้อง x อัตราต่อ ตรม.
//
// ผลลัพธ์ปัดเป็นทศนิยม 2 ตำแหน่งแบบปัดครึ่งขึ้น (roundToTwo)
// เช่น 33.334 → 33.33 | 33.335 → 33.34
export const calcCommonExpenses = (common_expenses_type, common_expenses, room_size) => {
  if (!common_expenses_type) return common_expenses;
  return roundToTwo((room_size || 0) * common_expenses);
};

// ค่าเบี้ยประกันอัคคีภัย — เก็บเฉพาะเดือนที่ตั้งไว้ (fire_month) เท่านั้น เดือนอื่นเป็น 0
export const calcFireCost = (setting, current_month) => (setting?.fire_month && String(current_month) === String(setting.fire_month) ? Number(setting.fire_cost) : 0);

// ยอดรวมของบิล 1 ใบ (ยังไม่รวมเบี้ยปรับและค่าต่อกลับมิเตอร์)
// ใช้สูตรเดียวกับฝั่ง backend — รายรับบวกทั้งหมด ลดหนี้เป็นตัวเดียวที่ลบออก
export const billTotal = ({ water_bill = 0, equipment_maintenance_fee = 0, common_expenses = 0, waste_cost = 0, cable_cost = 0, other_income = 0, add_debt = 0, fire_cost = 0, reduce_debt = 0 }) =>
  Number(water_bill) + Number(equipment_maintenance_fee) + Number(common_expenses) + Number(waste_cost) + Number(cable_cost) + Number(other_income) + Number(add_debt) + Number(fire_cost) - Number(reduce_debt);

// ============================================================
// แปลงรูปข้อมูล "ตั้งค่าอัตราค่าน้ำ" ระหว่าง API กับฟอร์มบนหน้าจอ
// ------------------------------------------------------------
// รูปที่ API ใช้            : ตัวเลขล้วน, common_expenses_type เป็น boolean,
//                            อัตราขั้นต่ำซ้อนอยู่ใน water_charge {}
// รูปที่ฟอร์มบนหน้าจอใช้    : ข้อความมี comma (แสดงผลสวย), type เป็น "0"/"1" (radio),
//                            อัตราขั้นต่ำแบนออกมาเป็น water_charge_* ระดับบนสุด
//
// ทั้ง 2 ทางรวมไว้ที่นี่ที่เดียว เพื่อให้เพิ่ม/แก้ field ทำที่เดียวจบ
// และเป็นจุดเดียวที่ต้องแก้ถ้าจะย้ายไปใช้ชื่อ field ชุดใหม่ในอนาคต
// ============================================================

// ตัดคอมมาออกแล้วแปลงเป็นตัวเลข (ค่าที่ผ่าน toFixed มาจะมีคอมมา)
export const toNumber = (value) => Number(String(value ?? 0).replace(/,/g, "")) || 0;

// ชื่อค่าใช้จ่ายที่แต่ละโครงการตั้งเองได้ + ค่าเริ่มต้น
export const COST_LABEL_DEFAULTS = {
  water_bill_text: "ค่าน้ำประปา",
  meter_connect_cost_text: "ค่าต่อมิเตอร์",
  equipment_maintenance_fee_text: "ค่าบริการอุปกรณ์",
  waste_cost_text: "ค่าขยะ",
  common_expenses_text: "ค่าส่วนกลาง",
  fire_cost_text: "ค่าเบี้ยประกัน",
  cable_cost_text: "ค่าเคเบิล",
};

// ช่องเงินที่ฟอร์มแสดงเป็นข้อความมีคอมมา
const MONEY_FIELDS = ["meter_convergence", "equipment_maintenance_fee", "common_expenses", "waste_cost", "cable_cost", "add_debt", "reduce_debt", "fire_cost"];

// API → ฟอร์ม
export const normalizeWaterSetting = (d, toFixed) => {
  const form = { ...d };

  MONEY_FIELDS.forEach((k) => (form[k] = toFixed(toNumber(d[k]))));

  // อัตราขั้นต่ำ: API ซ้อนไว้ใน water_charge {} ฟอร์มใช้แบบแบน
  form.water_charge_start = toNumber(d.water_charge?.water_charge_start);
  form.water_charge_end = toNumber(d.water_charge?.water_charge_end);
  form.water_charge_price = toFixed(toNumber(d.water_charge?.water_charge_price));

  form.common_expenses_type = d.common_expenses_type ? "1" : "0";
  form.water_calculate_type = Number(d.water_calculate_type) || 2;

  // รายการที่ว่างอยู่ ให้ใส่แถวเปล่า 1 แถวไว้ให้ผู้ใช้กรอกต่อ
  form.penalty_day_list = d.penalty_day_list?.length ? d.penalty_day_list : [""];
  form.penalty_month_list = d.penalty_month_list?.length
    ? d.penalty_month_list.map((i) => ({ month_start: Number(i.month_start), month_end: Number(i.month_end), penalty_year_rate: toFixed(toNumber(i.penalty_year_rate)) }))
    : [{ month_start: "", month_end: "", penalty_year_rate: "0.00" }];
  form.water_rate_list = d.water_rate_list?.length
    ? d.water_rate_list.map((i) => ({ water_rate_start: Number(i.water_rate_start), water_rate_end: Number(i.water_rate_end), water_rate_price: toFixed(toNumber(i.water_rate_price)) }))
    : [{ water_rate_start: 0.0, water_rate_end: 0.0, water_rate_price: "0.00" }];

  // ชื่อค่าใช้จ่ายที่ยังไม่ได้ตั้ง ใช้ชื่อเริ่มต้น
  Object.entries(COST_LABEL_DEFAULTS).forEach(([key, fallback]) => (form[key] = d[key] || fallback));

  return form;
};

// ฟอร์ม → API
export const denormalizeWaterSetting = (s, toFixed) => ({
  ...s,
  interest_adjustment: toNumber(s.interest_adjustment),
  meter_convergence: toNumber(s.meter_convergence),
  equipment_maintenance_fee: toNumber(s.equipment_maintenance_fee),
  common_expenses: toNumber(s.common_expenses),
  common_expenses_type: s.common_expenses_type === "1",
  waste_cost: toNumber(s.waste_cost),
  cable_cost: toNumber(s.cable_cost),
  add_debt: toNumber(s.add_debt),
  reduce_debt: toNumber(s.reduce_debt),
  fire_cost: toFixed(toNumber(s.fire_cost)),
  payment_deadline: s.payment_deadline || "0",
  water_charge_start: toNumber(s.water_charge_start),
  water_charge_end: toNumber(s.water_charge_end),
  water_charge_price: toNumber(s.water_charge_price),
  water_calculate_type: Number(s.water_calculate_type) || 2,
  // ตัดแถวเปล่าออกก่อนส่ง
  penalty_day_list: s.penalty_day_list.filter((i) => i !== "").map(Number),
  penalty_month_list: s.penalty_month_list
    .filter((i) => i.month_start !== "" && i.month_end !== "" && i.penalty_year_rate !== "")
    .map((i) => ({ month_start: Number(i.month_start), month_end: Number(i.month_end), penalty_year_rate: Number(i.penalty_year_rate) })),
  water_rate_list: s.water_rate_list
    .filter((i) => i.water_rate_start !== 0 && i.water_rate_end !== 0 && i.water_rate_price !== 0)
    .map((i) => ({ water_rate_start: Number(i.water_rate_start), water_rate_end: Number(i.water_rate_end), water_rate_price: Number(i.water_rate_price) })),
  start_balance_statement: toNumber(s.start_balance_statement),
  start_date_statement: s.start_date_statement || null,
});
